import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import env from '../../config/env';

interface Report {
  id: number;
  type: 'ad_review' | 'user_review';
  subject: string;
  reason: string;
  details?: string;
  status: string;
  created_at: string;
}

interface Meta {
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
}

const PER_PAGE = 10;

const statusColor: Record<string, string> = {
  pending:  '#f59e0b',
  reviewed: '#3b82f6',
  resolved: '#22c55e',
  rejected: '#ef4444',
};

const ReviewsPage: React.FC = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const userId = params.get('user_id');

  const [reviews, setReviews] = useState<Report[]>([]);
  const [meta, setMeta]       = useState<Meta>({ total: 0, per_page: PER_PAGE, current_page: 1, last_page: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage]       = useState(1);

  const fetchReviews = useCallback((p: number) => {
    if (!userId) { setLoading(false); return; }
    setLoading(true);
    axios
      .get(`${env.apiUrl}/getUserReviews`, { params: { user_id: userId, page: p, per_page: PER_PAGE } })
      .then((res) => {
        if (res.data.status) {
          setReviews(res.data.reviews);
          setMeta(res.data.meta);
        }
      })
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => { fetchReviews(page); }, [page, fetchReviews]);

  const goTo = (p: number) => {
    if (p < 1 || p > meta.last_page) return;
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const pageNumbers = (): (number | '…')[] => {
    const { current_page: cur, last_page: last } = meta;
    if (last <= 5) return Array.from({ length: last }, (_, i) => i + 1);
    const pages: (number | '…')[] = [1];
    if (cur > 3) pages.push('…');
    for (let i = Math.max(2, cur - 1); i <= Math.min(last - 1, cur + 1); i++) pages.push(i);
    if (cur < last - 2) pages.push('…');
    pages.push(last);
    return pages;
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.pageTitle}>Your Submitted Reviews</h1>

      <p style={styles.introText}>
        Below are the reviews you have submitted. Our team reviews each case carefully according to platform policies.
      </p>

      <div style={styles.noteBox}>
        <p style={styles.noteTitle}>Important Note</p>
        <p style={styles.noteText}>
          All reviews are reviewed confidentially. Submitting a review does not guarantee immediate action, as each case
          is evaluated based on evidence and policy standards.
        </p>
      </div>

      <div style={styles.sectionHeader}>
        <p style={styles.sectionLabel}>Report Details</p>
        {meta.total > 0 && (
          <p style={styles.totalLabel}>{meta.total} review{meta.total !== 1 ? 's' : ''}</p>
        )}
      </div>
      <div style={styles.divider} />

      {loading ? (
        <p style={styles.emptyText}>Loading…</p>
      ) : reviews.length === 0 ? (
        <p style={styles.emptyText}>No reviews submitted.</p>
      ) : (
        <>
          {reviews.map((r, i) => (
            <div key={`${r.type}-${r.id}`}>
              <div style={styles.reviewRow}>
                <div style={styles.rowTop}>
                  {/* <span style={{ ...styles.typeBadge, backgroundColor: r.type === 'ad_review' ? '#e0f2fe' : '#fce7f3', color: r.type === 'ad_review' ? '#0369a1' : '#be185d' }}>
                    {r.type === 'ad_review' ? 'Ad Report' : 'User Report'}
                  </span> */}
                  {/* <span style={{ ...styles.statusBadge, color: statusColor[r.status] ?? '#666' }}>
                    {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                  </span> */}
                </div>
                <p style={styles.reviewSubject}>Reported Account / Ad: {r.subject}</p>
                <p style={styles.reviewText}><span> Reason: </span> {r.reason}</p>
                {/* {r.details && (
                  <p style={styles.reviewText}><span style={styles.label}>Details:</span> {r.details}</p>
                )} */}
                <p style={styles.reviewMeta}>Date Submitted: {r.created_at}</p>
              </div>
              {i < reviews.length - 1 && <div style={styles.rowDivider} />}
            </div>
          ))}

          {/* Pagination */}
          {meta.last_page > 1 && (
            <div style={styles.pagination}>
              <button
                style={{ ...styles.pageBtn, opacity: page === 1 ? 0.35 : 1 }}
                onClick={() => goTo(page - 1)}
                disabled={page === 1}
              >
                ‹
              </button>

              {pageNumbers().map((p, i) =>
                p === '…' ? (
                  <span key={`ellipsis-${i}`} style={styles.ellipsis}>…</span>
                ) : (
                  <button
                    key={p}
                    style={{ ...styles.pageBtn, ...(p === page ? styles.pageBtnActive : {}) }}
                    onClick={() => goTo(p as number)}
                  >
                    {p}
                  </button>
                )
              )}

              <button
                style={{ ...styles.pageBtn, opacity: page === meta.last_page ? 0.35 : 1 }}
                onClick={() => goTo(page + 1)}
                disabled={page === meta.last_page}
              >
                ›
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    fontFamily: "'Segoe UI', Arial, sans-serif",
    backgroundColor: '#fff',
    minHeight: '100vh',
    padding: '24px 20px',
    boxSizing: 'border-box',
    maxWidth: 480,
    margin: '0 auto',
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: '#111',
    textAlign: 'center',
    marginBottom: 16,
    marginTop: 0,
  },
  introText: {
    fontSize: 14,
    color: '#333',
    textAlign: 'center',
    lineHeight: 1.7,
    marginBottom: 24,
  },
  noteBox: {
    textAlign: 'center',
    marginBottom: 28,
  },
  noteTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#111',
    marginBottom: 8,
    marginTop: 0,
  },
  noteText: {
    fontSize: 14,
    color: '#333',
    lineHeight: 1.7,
    margin: 0,
    textAlign: 'center',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  sectionLabel: {
    fontSize: 14,
    fontWeight: 700,
    color: '#111',
    margin: 0,
  },
  totalLabel: {
    fontSize: 12,
    color: '#888',
    margin: 0,
  },
  divider: {
    borderBottom: '2px solid #111',
    marginTop: 6,
    marginBottom: 14,
  },
  reviewRow: {
    paddingTop: 12,
    paddingBottom: 12,
  },
  rowTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  typeBadge: {
    fontSize: 11,
    fontWeight: 600,
    padding: '3px 8px',
    borderRadius: 12,
  },
  statusBadge: {
    fontSize: 11,
    fontWeight: 600,
  },
  reviewSubject: {
    fontSize: 14,
    // fontWeight: 600,
    color: '#111',
    margin: '0 0 6px',
  },
  reviewText: {
    fontSize: 14,
    color: '#444',
    margin: '3px 0',
    lineHeight: 1.6,
  },
  label: {
    fontWeight: 600,
    color: '#111',
  },
  reviewMeta: {
    fontSize: 11,
    color: '#999',
    margin: '6px 0 0',
  },
  rowDivider: {
    borderBottom: '1px solid #eee',
  },
  emptyText: {
    fontSize: 14,
    color: '#666',
    marginTop: 12,
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 6,
    marginTop: 28,
    paddingBottom: 16,
  },
  pageBtn: {
    minWidth: 34,
    height: 34,
    borderRadius: 8,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#333',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 8px',
  },
  pageBtnActive: {
    background: '#111',
    color: '#fff',
    borderColor: '#111',
    fontWeight: 700,
  },
  ellipsis: {
    fontSize: 14,
    color: '#999',
    padding: '0 2px',
  },
};

export default ReviewsPage;
